// Pure presentation helpers for the public driver cluster. See
// docs/adr/0006-present-low-signal-drivers.md — photo-less / review-less
// drivers stay visible; these helpers decide how they are presented.

// Structural subset of DriverProfile the cluster hero reads. Kept local so the
// helpers stay decoupled from the full API type and easy to fixture in tests.
type DriverPhoto = { small?: string | null; large?: string | null } | null
type HeroProfile = {
  data: { displayName?: string | null; profilePhoto?: DriverPhoto }
}

export interface CollageTile {
  alt: string
  filename: string
  desktopFilename: string
}

/**
 * First letter of the first and last word of a driver's display name,
 * uppercased. "Garry A" -> "GA", "Garry" -> "G", empty/blank -> "".
 */
export function getDriverInitials(displayName?: string | null): string {
  const words = (displayName ?? "").trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return ""
  // Array.from grabs whole code points, so astral/emoji first characters don't
  // split into a lone surrogate.
  const first = Array.from(words[0])[0]
  const last = Array.from(words[words.length - 1])[0]
  return (words.length === 1 ? first : first + last).toUpperCase()
}

/**
 * Deterministic, stable background color for a driver's monogram avatar,
 * derived from the driver id so the same driver always gets the same color
 * and a page full of photo-less drivers looks varied, not broken. Fixed
 * saturation/lightness keep contrast with white text predictable.
 */
export function getDriverAvatarColor(seed: string | number): string {
  const str = String(seed)
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0
  }
  const hue = Math.abs(hash) % 360
  // Lightness 28% keeps every hue dark enough for white monogram text to clear
  // WCAG AA (4.5:1) with margin — the worst case, pure yellow (hue 60), is
  // ~5.06:1. 32% dipped to 4.04:1 there; 45% failed the whole yellow/green
  // band. Verified by the contrast sweep in tests/lib/drivers/presentation.test.ts.
  return `hsl(${hue} 60% 28%)`
}

/**
 * Collage tiles for the cluster hero — photo'd drivers only, capped at 4.
 * Filters BEFORE slicing: a photo-less driver among the first four must not
 * shrink the mosaic (CollagePageHeader hides the mobile collage below 4 tiles).
 */
export function buildCollageTiles(profiles: HeroProfile[]): CollageTile[] {
  return profiles
    .filter((p) => Boolean(p.data.profilePhoto?.small))
    .slice(0, 4)
    .map((p) => ({
      alt: p.data.displayName ?? "Driver photo",
      filename: p.data.profilePhoto?.small ?? "",
      desktopFilename: p.data.profilePhoto?.large ?? "",
    }))
}

/**
 * The photo of the first driver on the page that actually has one, used for the
 * hero background and og/twitter images. Returns null when every driver on the
 * page is photo-less, so the caller can fall back to a stock image.
 */
export function pickHeroPhoto(
  profiles: HeroProfile[],
): { small: string; large: string; displayName: string } | null {
  const withPhoto = profiles.find((p) => Boolean(p.data.profilePhoto?.small))
  if (!withPhoto?.data.profilePhoto?.small) return null
  return {
    small: withPhoto.data.profilePhoto.small,
    large:
      withPhoto.data.profilePhoto.large ?? withPhoto.data.profilePhoto.small,
    // Lets the hero alt text name the person in the photo instead of asserting
    // a generic "pet transporters" label over whoever happens to be first.
    displayName: withPhoto.data.displayName?.trim() || "CitizenShipper driver",
  }
}
